import { useState } from 'preact/hooks'

import { passkeysMoeglich } from './passkeyEntsperren.js'

/**
 * Entsperrt den Schlüsselspeicher.
 *
 * ## Warum dieser Bildschirm überhaupt kommt
 *
 * Der Schlüssel für die lokalen Inhalte liegt nur im Arbeitsspeicher — beim
 * Neuladen, im neuen Tab, nach dem Kaltstart der App ist er weg. Die Sitzung
 * hält dagegen 30 Tage. Es gibt also viele Starts ohne Anmeldung, und genau
 * dort steht dieser Bildschirm.
 *
 * ## Der Passkey ist der Hauptweg, der Satz die Rückfallebene
 *
 * Bis Phase 18 lieferte nur die Anmeldezeremonie ein PRF-Geheimnis; wer
 * angemeldet blieb, tippte seinen Satz praktisch täglich. Für „Kind bis Oma"
 * (Anf. 75) war das der Punkt, an dem die App unbenutzbar wird. Jetzt fragt
 * die App den Passkey auch ohne Anmeldung.
 *
 * Der Satz bleibt: für Geräte ohne PRF-Unterstützung und als zweiter Weg, wenn
 * der Passkey verloren geht.
 */

/** Auf diesem Gerät kann der Authenticator kein PRF — nicht wieder anbieten. */
const KEIN_PRF = 'fh-prf-nicht-moeglich'
/** „Später" beim Angebot — einmal reicht, Nachfragen wäre Nörgeln. */
const NICHT_FRAGEN = 'fh-passkey-nicht-fragen'

function merker(name: string): boolean {
  try { return localStorage.getItem(name) === '1' } catch { return false }
}
function merken(name: string): void {
  try { localStorage.setItem(name, '1') } catch { /* Privatmodus: dann eben nicht */ }
}

export function Entsperren({
  neu, passkeyWeg, mitSatz, mitPasskey, passkeyNachruesten, onFertig,
}: {
  /** Erster Speicher auf diesem Gerät: Der Satz wird gesetzt, nicht abgefragt. */
  neu: boolean
  /** Ist der Passkey für DIESEN Speicher schon als Weg eingerichtet? */
  passkeyWeg: boolean
  mitSatz: (satz: string) => Promise<void>
  /** `false` = Zeremonie lief, der Authenticator liefert aber kein PRF. */
  mitPasskey: () => Promise<boolean>
  passkeyNachruesten: () => Promise<boolean>
  onFertig: () => void
}) {
  const [schritt, setSchritt] = useState<'entsperren' | 'anbieten'>('entsperren')
  const [passphrase, setPassphrase] = useState('')
  const [wiederholung, setWiederholung] = useState('')
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)

  const passkeyAngeboten = passkeysMoeglich() && !merker(KEIN_PRF)
  const passkeyKnopf = passkeyAngeboten && passkeyWeg && !neu

  function fertigOderAnbieten() {
    // Nach dem Satz: einmal anbieten, künftig den Finger zu nehmen. Das ist der
    // ganze Zweck der Übung — ein Schalter in den Einstellungen, den niemand
    // findet, hätte nichts geändert.
    if (passkeyAngeboten && !passkeyWeg && !merker(NICHT_FRAGEN)) {
      setSchritt('anbieten')
      return
    }
    onFertig()
  }

  async function satzAbsenden(e: Event) {
    e.preventDefault()
    setFehler(null)

    if (neu && passphrase !== wiederholung) {
      setFehler('Die beiden Eingaben stimmen nicht überein.')
      return
    }
    if (passphrase.length < 12) {
      setFehler('Bitte mindestens 12 Zeichen — ein kurzer Satz funktioniert gut.')
      return
    }

    setBusy(true)
    try {
      await mitSatz(passphrase)
      setPassphrase('')
      setWiederholung('')
      fertigOderAnbieten()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(false)
    }
  }

  async function passkeyVersuch() {
    setFehler(null)
    setHinweis(null)
    setBusy(true)
    try {
      if (await mitPasskey()) {
        onFertig()
        return
      }
      // Zeremonie geklappt, aber kein Geheimnis: Das Gerät kann es nicht.
      merken(KEIN_PRF)
      setHinweis('Dieses Gerät kann den Passkey dafür nicht nutzen. Bitte den Satz eingeben.')
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(false)
    }
  }

  async function nachruesten() {
    setFehler(null)
    setBusy(true)
    try {
      if (await passkeyNachruesten()) {
        onFertig()
        return
      }
      merken(KEIN_PRF)
      setHinweis('Dieses Gerät kann den Passkey dafür nicht nutzen. Der Satz bleibt.')
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(false)
    }
  }

  if (schritt === 'anbieten') {
    return (
      <div class="fh-anmelden">
        <h1 class="fh-h1">Nächstes Mal einfacher?</h1>
        <p class="fh-muted">
          Du kannst diesen Bildschirm künftig mit deinem Passkey öffnen — Finger
          auflegen statt tippen. Dein Satz bleibt daneben bestehen und gilt
          weiter.
        </p>

        {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}
        {hinweis && <p class="fh-hinweis fh-hinweis--info" role="status">{hinweis}</p>}

        <button class="fh-btn fh-btn--gross" disabled={busy} onClick={() => void nachruesten()}>
          <span aria-hidden="true">👆</span> {busy ? 'Einen Moment…' : 'Mit Passkey einrichten'}
        </button>
        <button
          class="fh-btn fh-btn--quiet" disabled={busy}
          onClick={() => { merken(NICHT_FRAGEN); onFertig() }}
        >
          Später
        </button>
      </div>
    )
  }

  return (
    <div class="fh-anmelden">
      <h1 class="fh-h1">{neu ? 'Deine Nachrichten schützen' : 'Nachrichten entsperren'}</h1>

      {passkeyKnopf && (
        <>
          <p class="fh-muted">Leg deinen Finger auf, um deine Nachrichten zu öffnen.</p>
          <button
            class="fh-btn fh-btn--gross" disabled={busy} autofocus
            onClick={() => void passkeyVersuch()}
          >
            <span aria-hidden="true">👆</span> {busy ? 'Einen Moment…' : 'Mit Passkey entsperren'}
          </button>
        </>
      )}

      {hinweis && <p class="fh-hinweis fh-hinweis--info" role="status">{hinweis}</p>}

      {neu ? (
        <p class="fh-muted">
          Wähle einen Satz, den nur du kennst. Er schützt deine Nachrichten auf
          diesem Gerät — auch vor dem Server. Ein Satz mit vier Wörtern ist
          sicherer und leichter zu merken als ein kompliziertes Passwort.
        </p>
      ) : (
        <p class="fh-muted">
          {passkeyKnopf
            ? 'Oder gib den Satz ein, mit dem du deine Nachrichten geschützt hast.'
            : 'Gib den Satz ein, mit dem du deine Nachrichten geschützt hast.'}
        </p>
      )}

      <form class="fh-card fh-anmelden__formular" onSubmit={(e) => void satzAbsenden(e)}>
        <label class="fh-feld">
          <span>{neu ? 'Dein Satz' : 'Satz'}</span>
          <input
            type="password" required autofocus={!passkeyKnopf}
            autocomplete={neu ? 'new-password' : 'current-password'}
            value={passphrase}
            placeholder="zum Beispiel: mein blauer Stuhl knarzt"
            onInput={(e) => setPassphrase((e.target as HTMLInputElement).value)}
          />
        </label>

        {neu && (
          <label class="fh-feld">
            <span>Noch einmal</span>
            <input
              type="password" required autocomplete="new-password" value={wiederholung}
              onInput={(e) => setWiederholung((e.target as HTMLInputElement).value)}
            />
          </label>
        )}

        {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}

        <button class="fh-btn" type="submit" disabled={busy}>
          {busy ? 'Einen Moment…' : neu ? 'Weiter' : 'Entsperren'}
        </button>
      </form>

      {neu && (
        <p class="fh-hinweis fh-hinweis--info">
          <span aria-hidden="true">ℹ️</span>
          <span>
            Wenn du diesen Satz vergisst und alle deine Geräte verlierst, sind
            deine alten Nachrichten weg — auch wir kommen dann nicht mehr daran.
            Im nächsten Schritt bekommst du dafür ein Notfall-Blatt.
          </span>
        </p>
      )}
    </div>
  )
}
